(() => {
  const SECTION_SELECTOR = '[data-buddy-before-after-slider]';
  const RANGE_SELECTOR = '[data-buddy-before-after-range]';
  const MEDIA_SELECTOR = '[data-buddy-before-after-media]';

  const clamp = (value) => Math.min(100, Math.max(0, Number(value) || 0));

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const prepareVideo = (video, shouldAutoplay, showControls = false) => {
    video.querySelector('img:not([alt])')?.setAttribute('alt', '');
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('loop', '');

    if (shouldAutoplay) {
      video.autoplay = true;
      video.setAttribute('autoplay', '');
      video.controls = false;
      video.removeAttribute('controls');
    } else {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.controls = showControls;
      video.toggleAttribute('controls', showControls);
      video.pause();
    }
  };

  const playVideo = (video, section) => {
    if (section.dataset.videoAutoplay === 'false' || section.dataset.motionPaused === 'true' || document.hidden) return;

    prepareVideo(video, true, false);
    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const syncVideos = (section) => {
    const videos = section.querySelectorAll('video');
    const shouldAutoplay = section.dataset.videoAutoplay !== 'false' && section.dataset.motionPaused !== 'true';
    const showControls = section.dataset.videoAutoplay === 'false';

    if (!videos.length) return;

    videos.forEach((video) => {
      prepareVideo(video, shouldAutoplay, showControls);

      if (!shouldAutoplay) {
        video.pause();
        return;
      }

      if (video.dataset.buddyBeforeAfterVideoReadyBound !== 'true') {
        video.addEventListener('loadedmetadata', () => playVideo(video, section));
        video.addEventListener('canplay', () => playVideo(video, section));
        video.dataset.buddyBeforeAfterVideoReadyBound = 'true';
      }

      playVideo(video, section);
    });
  };

  const initSection = (section) => {
    if (!section || section.dataset.buddyBeforeAfterInitialized === 'true') return;

    const range = section.querySelector(RANGE_SELECTOR);
    const media = section.querySelector(MEDIA_SELECTOR);
    const motionButton = section.querySelector('[data-buddy-before-after-motion-toggle]');
    if (!range || !media) return;

    let isDragging = false;
    let isMotionPaused = reduceMotion.matches;
    section.dataset.motionPaused = String(isMotionPaused);

    const updateMotionButton = () => {
      if (!motionButton) return;
      motionButton.setAttribute('aria-pressed', String(isMotionPaused));
      motionButton.setAttribute('aria-label', isMotionPaused ? 'Play before and after videos' : 'Pause before and after videos');
      motionButton.querySelector('[data-motion-pause-icon]')?.toggleAttribute('hidden', isMotionPaused);
      motionButton.querySelector('[data-motion-play-icon]')?.toggleAttribute('hidden', !isMotionPaused);
    };

    const setMotionPaused = (paused) => {
      isMotionPaused = paused;
      section.dataset.motionPaused = String(paused);
      updateMotionButton();
      syncVideos(section);
    };

    const updatePosition = () => {
      const position = clamp(range.value);
      section.style.setProperty('--buddy-ba-position', `${position}%`);
      range.setAttribute('aria-valuetext', `${position}% before image visible`);
    };

    const updatePositionFromClientX = (clientX) => {
      const rect = media.getBoundingClientRect();
      if (!rect.width) return;

      range.value = clamp(((clientX - rect.left) / rect.width) * 100);
      updatePosition();
    };

    const startDrag = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      isDragging = true;
      media.setPointerCapture?.(event.pointerId);
      updatePositionFromClientX(event.clientX);
      event.preventDefault();
    };

    const drag = (event) => {
      if (!isDragging) return;

      updatePositionFromClientX(event.clientX);
      event.preventDefault();
    };

    const endDrag = () => {
      isDragging = false;
    };

    media.addEventListener('pointerdown', startDrag);
    media.addEventListener('pointermove', drag);
    media.addEventListener('pointerup', endDrag);
    media.addEventListener('pointercancel', endDrag);
    range.addEventListener('input', updatePosition);
    range.addEventListener('change', updatePosition);
    range.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      range.value = clamp(Number(range.value) + direction * 5);
      updatePosition();
    });
    motionButton?.addEventListener('click', () => setMotionPaused(!isMotionPaused));
    reduceMotion.addEventListener?.('change', () => setMotionPaused(reduceMotion.matches));

    section.dataset.buddyBeforeAfterInitialized = 'true';
    updatePosition();
    syncVideos(section);

    if (section.dataset.videoAutoplay !== 'false') {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) syncVideos(section);
            else section.querySelectorAll('video').forEach((video) => video.pause());
          });
        }, { threshold: 0.2 });

        observer.observe(section);
      }

      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) syncVideos(section);
      });
      window.addEventListener('pageshow', () => syncVideos(section));
      ['pointerdown', 'touchstart', 'scroll'].forEach((eventName) => {
        window.addEventListener(eventName, () => syncVideos(section), { once: true, passive: true });
      });
    }

    updateMotionButton();
  };

  const initAll = (container = document) => {
    container.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
